// @ts-nocheck - Large configuration file with complex types, converted from JS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import timezones from "../timezones";
import contactUUID from "../../../../Contacts/contactUUID";
import {
  createCS,
  loadContactSetsPaginated,
  proxyAPI,
} from "../../../../proxyAPI";
import { store } from "../../../../store/store";
import {
  addNewBusinessHours,
  selectDataCategories,
  selectParticipantCategories,
} from "../../../../store/slices/projectSlice";
import { setCurrentModal } from "../../../../store/slices/modalsSlice";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { pageProps } from "../../../../helpers/pageProps";

const getOptionValues = (action, optionType) =>
  [...(action.Options || [])]
    .filter((actOpt) => actOpt.Type === optionType)
    .sort((a, b) => a.Order - b.Order)
    .map((opt) => opt.Value);

// COMMON INPUTS
export const valueFromInput = ({
  type = "option",
  field = "valueFrom",
  label = "Value from",
  ifPropertyHas = [],
  ifOptionTypeHas = [],
  isFixed = true,
  isDataCategory = true,
  isIgnore = true,
  defaultValue = isIgnore
    ? ["ignore"]
    : isFixed
    ? ["fixed"]
    : ["inputArgument"],
  optionDefaults = () => ({}),
  inputHelp = "Do you want to check a fixed value or a value from a variable or a data category",
  additionalInputs = {},
  isTab = false,
} = {}) => ({
  type,
  field,
  ifPropertyHas,
  ifOptionTypeHas,
  defaultValue,
  optionDefaults,
  props: {
    inputType: isTab ? "tab" : "select",
    isValidNewOption: () => false,
    label,
    inputHelp,
    options: [
      ...(isIgnore ? [{ value: "ignore", label: "None" }] : []),
      ...(isFixed ? [{ value: "fixed", label: "Fixed value" }] : []),
      { value: "inputArgument", label: "Variable" },
      ...(isDataCategory
        ? [{ value: "storageData:category", label: "Data category" }]
        : []),
      ...Object.keys(additionalInputs).map((k) => ({
        value: k,
        label: additionalInputs[k].props.label,
      })),
    ],
  },
});

export const inputArgumentInput = ({
  type = "option",
  field = "inputArgument",
  ifOptionTypeHas = [],
  ifPropertyHas = [],
  filterFn = () => true,
  optionDefaults = () => ({}),
} = {}) => ({
  // input arguments
  type,
  field,
  ifOptionTypeHas,
  ifPropertyHas,
  optionDefaults,
  props: {
    inputHelp: {
      id: "002",
      introElements: [1, 2],
      moreElements: [3, 4],
      includeIntro: true,
      sourceLink: "https://help.uatboomcomms.com/docs/next/Input-Arguments",
    },
    label: "Variable",
    inputType: "select",
    isValidNewOption: () => false,
    options: store
      .getState()
      .project.InputArguments.filter(filterFn)
      .map((arg) => ({
        value: arg.UUID,
        label: arg.Label,
      })),
  },
});

export const threadInput = ({
  ifOptionTypeHas = [],
  ifPropertyHas = [],
  field = "UseMainThread",
  type = "property",
  defaultValue = type === "property" ? [false] : ["CurrentThread"],
} = {}) => ({
  type,
  field,
  defaultValue,
  ifOptionTypeHas,
  ifPropertyHas,
  props: {
    label: "Thread",
    inputType: "select",
    isValidNewOption: () => false,
    options:
      type === "property"
        ? [
            {
              value: true,
              label: "on main thread",
            },
            {
              value: false,
              label: "on current thread",
            },
          ]
        : [
            {
              value: "MainThread",
              label: "on main thread",
            },
            {
              value: "CurrentThread",
              label: "on current thread",
            },
          ],
  },
});

export const dataCategoryInputs = ({
  dataField = "storageData:category",
  label = "Data category",
  isValidNewOption = () => false,
  ifOptionTypeHas = [],
  ifPropertyHas = [],
  threadField = "UseMainThread",
  threadType = "property",
  defaultValueThread,
  isMulti = true,
  isClearable,
  isThreadInput = true,
} = {}) => [
  {
    // data category
    type: "option",
    field: dataField,
    ifOptionTypeHas,
    ifPropertyHas,
    isDataCategory: true,
    props: {
      label,
      inputType: "select",
      isMulti,
      isClearable,
      isValidNewOption: (v) =>
        v &&
        isValidNewOption() &&
        !selectDataCategories(store.getState()).some(
          (dc) => dc === v.toUpperCase()
        ),
      onCreateOption: (option) => option.toUpperCase(),
      inputHelp: "Select a data category",
      options: selectDataCategories(store.getState()).map((cat) => ({
        value: cat,
        label: cat,
      })),
    },
  },
  ...(isThreadInput
    ? [
        threadInput({
          type: threadType,
          field: threadField,
          ifOptionTypeHas,
          ifPropertyHas,
          defaultValue: defaultValueThread,
        }),
      ]
    : []),
];

export const compoundInputs = ({
  context,
  field = "TimeZone",
  label = "Override timezone",
  inputHelp,
  inputProps = {},
  ifOptionTypeHas = [],
  ifPropertyHas = [],
  defaultValueFrom,
  defaultValue = null,
  defaultValueThread = null,
  isDataCategory = true,
  isDataCreatable = false,
  isIgnore = true,
  filterFn = (arg) => ["short-text"].includes(arg.Format),
  fromField = `${field}From`,
  isFixed = true,
  fixedField = `${field}Value`,
  inputArgumentField = `${field}Value`,
  dataField = `${field}Value`,
  threadField = `${field}Thread`,
  threadType = "option",
  optionDefaults = ([{ Value: v }]) => ({
    [fixedField || `${field}Value`]:
      // eslint-disable-next-line no-nested-ternary
      v === "fixed"
        ? ""
        : v === "inputArgument"
        ? store.getState()?.project.InputArguments.find(filterFn)?.UUID
        : null,
    ...(threadType === "option"
      ? {
          [threadField || `${field}Thread`]:
            v === "storageData:category" ? "CurrentThread" : null,
        }
      : {}),
  }),
  valueOptionDefaults = () => ({}),
  isMulti = true,
  additionalInputs = {},
  isValueFromTab = false,
}) => [
  valueFromInput({
    field: fromField,
    label,
    ifOptionTypeHas,
    ifPropertyHas,
    optionDefaults,
    isFixed,
    isDataCategory,
    isIgnore,
    inputHelp,
    additionalInputs,
    isTab: isValueFromTab,
    defaultValue: defaultValueFrom,
  }),
  ...Object.keys(additionalInputs).map((k) => ({
    ...additionalInputs[k],
    ifOptionTypeHas: [
      ...ifOptionTypeHas,
      {
        optionType: fromField,
        values: [k],
      },
    ],
  })),
  inputArgumentInput({
    context,
    type: "option",
    field: inputArgumentField,
    filterFn,
    optionDefaults: valueOptionDefaults,
    ifPropertyHas,
    ifOptionTypeHas: [
      ...ifOptionTypeHas,
      {
        optionType: fromField,
        values: ["inputArgument"],
      },
    ],
  }),
  ...dataCategoryInputs({
    context,
    dataField,
    isValidNewOption: () => isDataCreatable,
    threadType,
    threadField,
    defaultValueThread,
    optionDefaults: valueOptionDefaults,
    ifPropertyHas,
    isMulti,
    ifOptionTypeHas: [
      ...ifOptionTypeHas,
      {
        optionType: fromField,
        values: ["storageData:category"],
      },
    ],
  }),
  {
    // fixed value
    type: "option",
    field: fixedField,
    ifPropertyHas,
    ifOptionTypeHas: [
      ...ifOptionTypeHas,
      {
        optionType: fromField,
        values: ["fixed"],
      },
    ],
    defaultValue,
    optionDefaults: valueOptionDefaults,
    props: {
      label: `${field} value`,
      ...inputProps,
    },
  },
];

export const participantCategoriesInputs = ({
  context,
  type = "option",
  field = "storageRecipient:category",
  isValidNewOption = () => false,
  ifOptionTypeHas = [],
  ifPropertyHas = [],
  threadType,
  threadField,
}) => [
  {
    // participant categories
    type,
    field,
    ifOptionTypeHas,
    ifPropertyHas,
    props: {
      label: "Participant category",
      inputType: "select",
      isMulti: true,
      isValidNewOption: (v) => v && isValidNewOption(),
      inputHelp: "Select a participant category",
      options: selectParticipantCategories(store.getState()).map((cat) => ({
        value: cat,
        label: cat,
      })),
      onCreateOption: (option) => option.toUpperCase(),
    },
  },
  threadInput({
    ifOptionTypeHas,
    ifPropertyHas,
    type: threadType,
    field: threadField,
  }),
];

export const contactSetInputs = ({
  type = "property",
  field = "TypeUUID",
  action,
  isValidNewOption = () => false,
  ifOptionTypeHas = [],
}) => [
  {
    type,
    field,
    ifOptionTypeHas,
    children: action.TypeUUID && (
      <button
        className="btn btn-link px-0"
        type="button"
        onClick={() =>
          store.dispatch(
            setCurrentModal({
              modal: "contactSet",
              data: { uuid: action.TypeUUID },
            })
          )
        }
      >
        <small>
          <FontAwesomeIcon icon={faEdit} /> Edit group
        </small>
      </button>
    ),
    props: {
      label: "Group",
      inputType: "select",
      isMulti: false,
      isValidNewOption,
      inputHelp: "Select a group",
      loadOptions: loadContactSetsPaginated({ contactSetType: "static" }),
      getValueLabels: async (vs) => {
        const values = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const v of vs) {
          if (v) {
            // eslint-disable-next-line no-await-in-loop
            const data = await proxyAPI({
              prefix: "contacts/v1",
              url: `/contact-set/${v}`,
            });
            values.push({ value: data.uuid, label: data.label });
          }
        }
        return values;
      },
      isContactSet: true,
      onCreateOption: (option) => {
        const uuid = contactUUID();
        createCS(option, uuid);
        store.dispatch(
          setCurrentModal({
            modal: "contactSet",
            data: { uuid },
          })
        );
        return uuid;
      },
    },
  },
];

const timeConversion = {
  day: 1,
  hour: 24,
  minute: 24 * 60,
  second: 24 * 60 * 60,
};

export const milestoneInputs = ({ context, rule, action }) => [
  {
    type: "heading",
    label: "Milestone options",
  },
  // milestone type interval/dateTime/useMilestone
  {
    type: "option",
    field: "MilestoneType",
    defaultValue: ["interval"],
    optionDefaults: ([{ Value: value }]) => {
      const date = new Date();
      return value === "interval"
        ? {
            variable: { ValueFrom: "static", Value: "1" },
            MilestoneTimeZoneFrom: null,
            OffsetFrom: null,
            offsetType: null,
            offset: null,
            timezone: null,
          }
        : {
            variable: {
              ValueFrom: "static",
              Value: `${date.getFullYear().toString().padStart(4, "0")}-${(
                date.getMonth() + 1
              )
                .toString()
                .padStart(2, "0")}-${date
                .getDate()
                .toString()
                .padStart(2, "0")} ${date
                .getHours()
                .toString()
                .padStart(2, "0")}:${date
                .getMinutes()
                .toString()
                .padStart(2, "0")}`,
            },
            timezone: { ValueFrom: "static", Value: "" },
            offset: { ValueFrom: "static", Value: "0" },
            offsetType: "positive",
          };
    },
    props: {
      label: "Milestone type",
      inputType: "select",
      isValidNewOption: () => false,
      options: [
        { label: "Interval", value: "interval" },
        { label: "Date/time", value: "dateTime" },
      ],
    },
  },

  ...(getOptionValues(action, "MilestoneType")[0] === "dateTime"
    ? [
        {
          type: "compound",
          field: `MilestoneValue`,
          defaultValue: ["1"],
          ifOptionTypeHas: [
            { optionType: "MilestoneType", values: ["dateTime"] },
          ],
          props: {
            label: "Milestone value",
            inputArgumentProps: { Format: "dateTime" },
            isValidNewOption: (v) => v.trim(),
            inputType: "dateTime",
            dateTimeType: "dateTime",
            isDateTimeUTC: false,
          },
        },
      ]
    : []),

  // ...(getOptionValues(action, "MilestoneType")[0] === "interval" ? [
  {
    type: "compound",
    field: `MilestoneValue`,
    defaultValue: ["1"],
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["interval"] }],
    props: {
      label: "Milestone value",
      inputArgumentProps: { Format: "number" },
      isValidNewOption: (v) => v.trim(),
      type: "number",
      min: 0,
    },
  },
  // ] : []),

  // show milestone unit
  {
    type: "property",
    field: "WithOffsetUnit",
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["interval"] }],
    defaultValue: ["minute"],
    optionDefaults: ([value]) =>
      getOptionValues(action, "MilestoneValue")[0] >
      store.getState().features.featuresMaxMilestone * timeConversion[value]
        ? { MilestoneValue: { ValueFrom: "static", Value: "1" } }
        : {},
    props: {
      label: "Milestone unit",
      inputType: "select",
      isValidNewOption: () => false,
      options: [
        ...(pageProps.isLocal ? [{ value: "second", label: "Second" }] : []),
        { value: "minute", label: "Minute" },
        { value: "hour", label: "Hour" },
        { value: "day", label: "Day" },
      ],
    },
  },

  // show time select if variable is date only
  ...(store
    .getState()
    ?.project.InputArguments.find(
      (ia) => ia.UUID === getOptionValues(action, "MilestoneValue")[0]
    )?.Format === "date"
    ? compoundInputs({
        context,
        field: "MilestoneTime",
        label: "Milestone time from",
        isDataCategory: false,
        isIgnore: false,
        ifOptionTypeHas: [
          { optionType: "MilestoneType", values: ["dateTime"] },
          { optionType: "MilestoneFrom", values: ["inputArgument"] },
        ],
        filterFn: (arg) => ["time"].includes(arg.Format),
        inputProps: {
          dateTimeType: "time",
        },
      })
    : []),
  // milestone time zone
  {
    type: "heading",
    label: "Timezone override options",
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["dateTime"] }],
  },
  {
    type: "compound",
    field: `timezone`,
    defaultValue: [""],
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["dateTime"] }],
    props: {
      label: "Timezone override",
      inputArgumentProps: { Format: "short-text" },
      isValidNewOption: (v) => v.trim(),
      inputType: "select",
      options: [
        { value: "", label: `Project default (${pageProps.defaultTZ})` },
        ...timezones.map((tz) => ({
          value: tz.tzCode,
          label: tz.label,
        })),
      ],
    },
  },

  // Offset options
  {
    type: "heading",
    label: "Offset options",
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["dateTime"] }],
  },

  {
    type: "compound",
    field: `offset`,
    defaultValue: ["0"],
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["dateTime"] }],
    props: {
      label: "Milestone offset value",
      inputArgumentProps: { Format: "number" },
      isValidNewOption: (v) => v.trim(),
      type: "number",
      min: 0,
    },
  },

  {
    type: "option",
    field: "offsetType",
    defaultValue: ["positive"],
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["dateTime"] }],
    props: {
      label: "Offset type",
      inputType: "select",
      isValidNewOption: () => false,
      options: [
        { value: "positive", label: "Positive" },
        { value: "negative", label: "Negative" },
      ],
    },
  },
  // show milestone unit
  {
    type: "property",
    field: "WithOffsetUnit",
    defaultValue: ["minute"],
    ifOptionTypeHas: [{ optionType: "MilestoneType", values: ["dateTime"] }],
    props: {
      label: "Offset unit",
      inputType: "select",
      isValidNewOption: () => false,
      options: [
        ...(pageProps.isLocal ? [{ value: "second", label: "Second" }] : []),
        { value: "minute", label: "Minute" },
        { value: "hour", label: "Hour" },
        { value: "day", label: "Day" },
      ],
    },
  },
];

export const businessHoursInputs = ({ context, action, params }) => {
  const { featuresBusinessHours } = store.getState().features;

  return featuresBusinessHours
    ? [
        {
          type: "heading",
          label: "Business hours",
          className: "mb-n3",
        },
        {
          type: "property",
          field: "BusinessHoursUUID",
          children: action.BusinessHoursUUID && (
            <Link
              to={`/library/logic/builder/${params.projectID}/business-hours/${action?.BusinessHoursUUID}`}
              state={{ nextPage: -1 }}
            >
              <small>
                <FontAwesomeIcon icon={faEdit} /> Edit business hours
              </small>
            </Link>
          ),
          optionDefaults: ([v]) =>
            !v
              ? {
                  TimeZone: null,
                }
              : {
                  TimeZone: { Value: "", ValueFrom: "static" },
                },
          props: {
            label: "Business hours",
            inputType: "select",
            maxLength: 255,
            options: [
              {
                value: "",
                label: "None selected.",
              },
              ...store.getState().project.BusinessHours.map((bh) => ({
                value: bh?.UUID,
                label: bh?.Label || bh?.UUID,
              })),
            ],
            isValidNewOption: (v) => v,
            onCreateOption: (value) => {
              const newUUID = uuidv4();
              store.dispatch(
                addNewBusinessHours({ UUID: newUUID, Label: value })
              );
              return newUUID;
            },
          },
        },
        ...(action.BusinessHoursUUID
          ? [
              {
                type: "compound",
                field: `TimeZone`,
                defaultValue: [""],
                props: {
                  label: "Select time zone",
                  inputArgumentProps: {
                    Format: "configuration",
                    DataStructure: "Time Zones",
                  },
                  isValidNewOption: (v) => v.trim(),
                  inputType: "select",
                  options: [
                    {
                      value: "",
                      label: `Project default (${pageProps.defaultTZ})`,
                    },
                    ...timezones.map((tz) => ({
                      value: tz.tzCode,
                      label: tz.label,
                    })),
                  ],
                },
              },
            ]
          : []),
      ]
    : [];
};
